import { type RefObject, useEffect } from 'react';

// The mark's light: ONE requestAnimationFrame loop drives every specular cue
// on the resting logo — the pointer-follow reflection ring, the radial sheen
// glint and the two hairline edge glints — from a single "light direction".
//
// Two sources feed that direction:
//   • POINTER (priority): when a fine pointer is NEAR the mark, the light
//     points at it — the original pointer-follow behaviour, maths unchanged.
//   • AMBIENT, EPISODIC (the default state): the glint is essentially still
//     for a random 4–9s (a barely perceptible drift), then SWEEPS — the light
//     travels a substantial arc across the top of the glass in ~350–700ms on
//     an in-out curve, brightening mid-sweep like light refracting quickly
//     off glass — and settles at once (no bounce) into a new quiet phase. One
//     sweep in four is followed by a short second flick. Arc, direction,
//     duration and catch are randomised per episode; the first sweep comes
//     ~2–4s after the entrance settles. (User, 12:48.)
// The two blend over POINTER_BLEND_MS in both directions.
//
// Efficiency: imperative attribute writes only (no React re-renders per
// frame); the ambient loop runs only once the entrance has settled, only
// while the document is visible; quiet-phase frames write at ~10fps
// (AMBIENT_QUIET_FRAME_MS), sweeps and pointer frames at full rate; and it
// does not run at all under prefers-reduced-motion (the glint is then static,
// exactly as it was before this loop existed).

// ---- pointer-follow (unchanged behaviour) ---------------------------------
const GRADIENT_CENTRE_X = 24;
const GRADIENT_CENTRE_Y = 24; // full-logo centre (the path spans 0..48 on both axes)
const GRADIENT_RADIUS = 34; // reaches the 48x48 logo's corners from its centre
export const MAX_HIGHLIGHT_OPACITY = 0.4;
export const FOLLOW_TIME_MS = 450;
const SETTLE_THRESHOLD = 0.005;
const CENTRE_GUARD_PX = 2;

// ---- ambient: episodic sweeps ---------------------------------------------
// Angles are radians in SVG orientation (0 = right, -π/2 = straight up).
export const AMBIENT_ANGLE_CENTRE_RAD = -1.92; // ≈ -110°: up and a little left — the resting glint corner
// The light stays on the top of the glass, reaching a little past the sides.
export const AMBIENT_ANGLE_MIN_RAD = (-200 * Math.PI) / 180;
export const AMBIENT_ANGLE_MAX_RAD = (20 * Math.PI) / 180;
// Quiet phase: random length; drift ≤ ~2px/s at the sheen's ~32px radius.
export const AMBIENT_QUIET_MIN_S = 4;
export const AMBIENT_QUIET_MAX_S = 9;
export const AMBIENT_FIRST_SWEEP_MIN_S = 2;
export const AMBIENT_FIRST_SWEEP_MAX_S = 4;
export const AMBIENT_QUIET_DRIFT_MAX_RAD_S = 0.06; // 0.06 rad/s × 32px ≈ 1.9px/s
// Sweep: arc, duration and mid-sweep brightness catch, each drawn per episode.
export const AMBIENT_SWEEP_ARC_MIN_RAD = (60 * Math.PI) / 180;
export const AMBIENT_SWEEP_ARC_MAX_RAD = (150 * Math.PI) / 180;
export const AMBIENT_SWEEP_MIN_MS = 350;
export const AMBIENT_SWEEP_MAX_MS = 700;
export const AMBIENT_SWEEP_CATCH_MIN = 0.2; // +20%…
export const AMBIENT_SWEEP_CATCH_MAX = 0.3; // …+30% at mid-sweep (the ±30% band's ceiling)
// One sweep in four is followed by a short second flick.
export const AMBIENT_DOUBLE_CHANCE = 0.25;
export const AMBIENT_DOUBLE_GAP_MS = 90;
export const AMBIENT_FLICK_ARC_MIN_RAD = (20 * Math.PI) / 180;
export const AMBIENT_FLICK_ARC_MAX_RAD = (50 * Math.PI) / 180;
export const AMBIENT_FLICK_MIN_MS = 220;
export const AMBIENT_FLICK_MAX_MS = 320;
export const AMBIENT_FLICK_CATCH = 0.12;
// Sheen geometry: distance of its centre from the logo's centre in
// bounding-box units (0.5 = the edge); it tightens a little mid-sweep.
export const AMBIENT_RADIUS = 0.32;
export const AMBIENT_SWEEP_RADIUS_TIGHTEN = 0.03;
// The reflection ring at rest: a faint white hint, brightening with the catch.
export const AMBIENT_RING_OPACITY = 0.08;
export const AMBIENT_RING_CATCH_BOOST = 0.1;
export const POINTER_BLEND_MS = 400;
export const AMBIENT_QUIET_FRAME_MS = 100; // ~10fps while the light is still
// Edge-glint gradient geometry (bounding-box units): from the lit rim point
// to just short of the centre — the original was (0,0) -> (0.65,0.65), i.e.
// the top-left corner at radius √0.5 pointing inward by 0.92 of that.
const EDGE_RIM_RADIUS = Math.SQRT1_2;
const EDGE_INNER_RADIUS = EDGE_RIM_RADIUS * (1 - 0.92);
const BRIGHTNESS_MAX = 1.3;

type Sweep = {
  startAt: number;
  endAt: number;
  from: number;
  to: number;
  catchAmp: number;
  double: boolean;
};

export type WorkHomeMarkGlintRefs = {
  markRef: RefObject<SVGSVGElement>;
  highlightRef: RefObject<SVGGElement>;
  directionRef: RefObject<SVGLinearGradientElement>;
  sheenRef: RefObject<SVGRadialGradientElement>;
  edgeLightRef: RefObject<SVGLinearGradientElement>;
  edgeShadowRef: RefObject<SVGLinearGradientElement>;
};

export type WorkHomeMarkGlintOptions = WorkHomeMarkGlintRefs & {
  /** Flips true when the entrance has landed; the ambient loop waits for it. */
  hasSettledRef: RefObject<boolean>;
  /** False under prefers-reduced-motion: the glint stays static. */
  ambientEnabled: boolean;
  /** Resting opacities the sweep's catch brightens from. */
  baseOpacity: { sheenNear: number; sheenMid: number; edgeLightPeak: number };
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const mix = (a: number, b: number, t: number) => a * (1 - t) + b * t;
const between = (min: number, max: number) => min + Math.random() * (max - min);
// ease-in-out cubic: quick through the middle, arrives with zero velocity —
// a fast pass that stops dead, no bounce.
const easeInOutCubic = (p: number) =>
  p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;

const planSweep = (
  now: number,
  from: number,
  arc: number,
  durationMs: number,
  catchAmp: number,
  double: boolean
): Sweep => {
  let to = from + (Math.random() < 0.5 ? -arc : arc);
  if (to < AMBIENT_ANGLE_MIN_RAD || to > AMBIENT_ANGLE_MAX_RAD) to = from - (to - from);
  to = clamp(to, AMBIENT_ANGLE_MIN_RAD, AMBIENT_ANGLE_MAX_RAD);
  return { startAt: now, endAt: now + durationMs, from, to, catchAmp, double };
};

export const useWorkHomeMarkGlint = ({
  markRef,
  highlightRef,
  directionRef,
  sheenRef,
  edgeLightRef,
  edgeShadowRef,
  hasSettledRef,
  ambientEnabled,
  baseOpacity,
}: WorkHomeMarkGlintOptions): void => {
  useEffect(() => {
    const mark = markRef.current;
    const highlight = highlightRef.current;
    const direction = directionRef.current;
    if (!mark || !highlight || !direction || !window.matchMedia) return;
    const sheen = sheenRef.current;
    const edgeLight = edgeLightRef.current;
    const edgeShadow = edgeShadowRef.current;
    const sheenStops = sheen ? Array.from(sheen.querySelectorAll('stop')) : [];
    const edgeLightStop = edgeLight?.querySelector('stop') ?? null;

    const media = window.matchMedia(
      '(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)'
    );
    let frame: number | null = null;
    // Quiet-phase wakeups go through a timer, not rAF — see `scheduleQuietTick`.
    let quietTimer: number | null = null;
    let pendingPointer: { clientX: number; clientY: number } | null = null;
    let previousTimestamp: number | null = null;
    let angle = 0;
    let targetAngle = 0;
    let proximity = 0;
    let targetProximity = 0;

    // Ambient state.
    let ambientStart: number | null = null;
    let quietUntil = 0;
    let sweep: Sweep | null = null;
    let driftRate = 0;
    let lastAmbientWrite = -Infinity;
    let pointerWeight = 0; // 0 = ambient light, 1 = pointer light
    let ambientAngle = AMBIENT_ANGLE_CENTRE_RAD + between(-0.4, 0.4);
    let ambientCatch = 0; // 0..1 of the current episode's peak
    let ambientRadius = AMBIENT_RADIUS;
    let ambientOpacityMul = 1;
    let sweeping = false;
    const ambientOn = () =>
      ambientEnabled && hasSettledRef.current === true && !document.hidden;

    const writeGlints = (lightAngle: number, radius: number, opacityMul: number) => {
      const cos = Math.cos(lightAngle);
      const sin = Math.sin(lightAngle);
      if (sheen) {
        sheen.setAttribute('cx', (0.5 + radius * cos).toFixed(4));
        sheen.setAttribute('cy', (0.5 + radius * sin).toFixed(4));
        sheenStops[0]?.setAttribute(
          'stop-opacity',
          (baseOpacity.sheenNear * opacityMul).toFixed(4)
        );
        sheenStops[1]?.setAttribute(
          'stop-opacity',
          (baseOpacity.sheenMid * opacityMul).toFixed(4)
        );
      }
      if (edgeLight) {
        edgeLight.setAttribute('x1', (0.5 + EDGE_RIM_RADIUS * cos).toFixed(4));
        edgeLight.setAttribute('y1', (0.5 + EDGE_RIM_RADIUS * sin).toFixed(4));
        edgeLight.setAttribute('x2', (0.5 + EDGE_INNER_RADIUS * cos).toFixed(4));
        edgeLight.setAttribute('y2', (0.5 + EDGE_INNER_RADIUS * sin).toFixed(4));
        edgeLightStop?.setAttribute(
          'stop-opacity',
          (baseOpacity.edgeLightPeak * opacityMul).toFixed(4)
        );
      }
      if (edgeShadow) {
        // The shadow rim sits opposite the light.
        edgeShadow.setAttribute('x1', (0.5 - EDGE_RIM_RADIUS * cos).toFixed(4));
        edgeShadow.setAttribute('y1', (0.5 - EDGE_RIM_RADIUS * sin).toFixed(4));
        edgeShadow.setAttribute('x2', (0.5 - EDGE_INNER_RADIUS * cos).toFixed(4));
        edgeShadow.setAttribute('y2', (0.5 - EDGE_INNER_RADIUS * sin).toFixed(4));
      }
    };

    const hideImmediately = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      if (quietTimer !== null) window.clearTimeout(quietTimer);
      quietTimer = null;
      pendingPointer = null;
      previousTimestamp = null;
      proximity = 0;
      targetProximity = 0;
      highlight.setAttribute('opacity', '0');
    };

    // Pointer-only write (ambient off): identical to the original behaviour.
    const writePointer = () => {
      const x = GRADIENT_RADIUS * Math.cos(angle);
      const y = GRADIENT_RADIUS * Math.sin(angle);
      direction.setAttribute('x1', String(GRADIENT_CENTRE_X - x));
      direction.setAttribute('y1', String(GRADIENT_CENTRE_Y - y));
      direction.setAttribute('x2', String(GRADIENT_CENTRE_X + x));
      direction.setAttribute('y2', String(GRADIENT_CENTRE_Y + y));
      highlight.setAttribute(
        'opacity',
        proximity === 0 ? '0' : String(MAX_HIGHLIGHT_OPACITY * proximity)
      );
    };

    // Blended write (ambient on): the light direction mixes ambient and
    // pointer by `pointerWeight`; every glint follows it.
    const writeBlended = () => {
      const w = pointerWeight;
      const ex = mix(Math.cos(ambientAngle), Math.cos(angle), w);
      const ey = mix(Math.sin(ambientAngle), Math.sin(angle), w);
      const lightAngle = Math.atan2(ey, ex);
      const x = GRADIENT_RADIUS * Math.cos(lightAngle);
      const y = GRADIENT_RADIUS * Math.sin(lightAngle);
      direction.setAttribute('x1', String(GRADIENT_CENTRE_X - x));
      direction.setAttribute('y1', String(GRADIENT_CENTRE_Y - y));
      direction.setAttribute('x2', String(GRADIENT_CENTRE_X + x));
      direction.setAttribute('y2', String(GRADIENT_CENTRE_Y + y));
      const ambientRing = AMBIENT_RING_OPACITY + AMBIENT_RING_CATCH_BOOST * ambientCatch;
      const pointerRing = MAX_HIGHLIGHT_OPACITY * proximity;
      highlight.setAttribute('opacity', mix(ambientRing, pointerRing, w).toFixed(4));
      writeGlints(lightAngle, mix(ambientRadius, AMBIENT_RADIUS, w), ambientOpacityMul);
    };

    const startSweep = (now: number, from: number) =>
      planSweep(
        now,
        from,
        between(AMBIENT_SWEEP_ARC_MIN_RAD, AMBIENT_SWEEP_ARC_MAX_RAD),
        between(AMBIENT_SWEEP_MIN_MS, AMBIENT_SWEEP_MAX_MS),
        between(AMBIENT_SWEEP_CATCH_MIN, AMBIENT_SWEEP_CATCH_MAX),
        Math.random() < AMBIENT_DOUBLE_CHANCE
      );

    const startFlick = (now: number, from: number) =>
      planSweep(
        now + AMBIENT_DOUBLE_GAP_MS,
        from,
        between(AMBIENT_FLICK_ARC_MIN_RAD, AMBIENT_FLICK_ARC_MAX_RAD),
        between(AMBIENT_FLICK_MIN_MS, AMBIENT_FLICK_MAX_MS),
        AMBIENT_FLICK_CATCH,
        false
      );

    const beginQuiet = (now: number) => {
      sweep = null;
      quietUntil = now + between(AMBIENT_QUIET_MIN_S, AMBIENT_QUIET_MAX_S) * 1000;
      driftRate = between(-AMBIENT_QUIET_DRIFT_MAX_RAD_S, AMBIENT_QUIET_DRIFT_MAX_RAD_S);
    };

    const updateAmbient = (timestamp: number, elapsed: number) => {
      if (ambientStart === null) {
        ambientStart = timestamp;
        quietUntil =
          timestamp +
          between(AMBIENT_FIRST_SWEEP_MIN_S, AMBIENT_FIRST_SWEEP_MAX_S) * 1000;
        driftRate = between(
          -AMBIENT_QUIET_DRIFT_MAX_RAD_S,
          AMBIENT_QUIET_DRIFT_MAX_RAD_S
        );
      }
      let catchShape = 0;
      if (sweep) {
        const p = clamp(
          (timestamp - sweep.startAt) / (sweep.endAt - sweep.startAt),
          0,
          1
        );
        ambientAngle = mix(sweep.from, sweep.to, easeInOutCubic(p));
        catchShape = Math.sin(Math.PI * p) * sweep.catchAmp;
        sweeping = timestamp >= sweep.startAt;
        if (p >= 1) {
          ambientAngle = sweep.to;
          catchShape = 0;
          if (sweep.double) sweep = startFlick(timestamp, sweep.to);
          else beginQuiet(timestamp);
        }
      } else {
        sweeping = false;
        ambientAngle = clamp(
          ambientAngle + (driftRate * elapsed) / 1000,
          AMBIENT_ANGLE_MIN_RAD,
          AMBIENT_ANGLE_MAX_RAD
        );
        if (timestamp >= quietUntil) sweep = startSweep(timestamp, ambientAngle);
      }
      ambientCatch = catchShape / AMBIENT_SWEEP_CATCH_MAX;
      ambientOpacityMul = Math.min(BRIGHTNESS_MAX, 1 + catchShape);
      ambientRadius = AMBIENT_RADIUS - AMBIENT_SWEEP_RADIUS_TIGHTEN * ambientCatch;
      // Pointer priority: blend toward the pointer while it is near, back when
      // it leaves — both over POINTER_BLEND_MS.
      const weightTarget = targetProximity > 0 || proximity > 0 ? 1 : 0;
      pointerWeight +=
        (weightTarget - pointerWeight) * (1 - Math.exp(-elapsed / POINTER_BLEND_MS));
      if (Math.abs(weightTarget - pointerWeight) < SETTLE_THRESHOLD)
        pointerWeight = weightTarget;
    };

    function schedule() {
      if (frame !== null) return;
      // A pointer event mid-quiet-phase promotes the pending timer to a frame
      // now, rather than leaving both scheduled.
      if (quietTimer !== null) {
        window.clearTimeout(quietTimer);
        quietTimer = null;
      }
      previousTimestamp = null;
      frame = requestAnimationFrame(paint);
    }

    // A quiet phase writes at AMBIENT_QUIET_FRAME_MS (~10fps), so keeping a
    // 60Hz rAF alive for it burned ~50 wakeups a second to do nothing and kept
    // the frame loop from idling for every mounted mark. Wait on a timer at
    // the write cadence instead and hop to ONE rAF to do the write, so the
    // write still lands on a frame boundary. Sweeps and pointer tracking stay
    // on continuous rAF (see the tail of `paint`).
    //
    // The wait is CAPPED BY THE NEXT DEADLINE, never a flat 100ms: the loop is
    // always awake within one frame of the moment a sweep or flick is due, so
    // the schedule computed by `startSweep`/`startFlick`/`beginQuiet` plays
    // out exactly as it did on a continuous rAF. This matters twice over —
    // AMBIENT_DOUBLE_GAP_MS is 90ms, SHORTER than the quiet cadence, so a
    // flat wait could have stretched the gap inside a double flick and
    // changed how it reads.
    //
    // Consequence, accepted: a mid-quiet tick's `elapsed` is ~100ms and the
    // guard in `paint` clamps it to 64ms, so quiet DRIFT advances at ~64% of
    // its nominal rate — at AMBIENT_QUIET_DRIFT_MAX_RAD_S that is ~1.2px/s
    // instead of ~1.9px/s, both far inside "essentially still", and drift is
    // the one quantity with no scheduled moment to hit. Raising the clamp
    // instead would change pointer-follow smoothing after a dropped frame,
    // which is real motion the user has signed off.
    const scheduleQuietTick = (msUntilNextDeadline: number) => {
      if (frame !== null || quietTimer !== null) return;
      const delay = Math.max(0, Math.min(AMBIENT_QUIET_FRAME_MS, msUntilNextDeadline));
      quietTimer = window.setTimeout(() => {
        quietTimer = null;
        frame = requestAnimationFrame(paint);
      }, delay);
    };

    const paint = (timestamp: number) => {
      frame = null;
      const ambient = ambientOn();
      if (document.hidden || (!media.matches && !ambient)) {
        hideImmediately();
        return;
      }

      const pointer = media.matches ? pendingPointer : null;
      pendingPointer = null;
      if (pointer) {
        const bounds = mark.getBoundingClientRect();
        if (bounds.width && bounds.height) {
          const dx = pointer.clientX - (bounds.left + bounds.width / 2);
          const dy = pointer.clientY - (bounds.top + bounds.height / 2);
          const distance = Math.hypot(dx, dy);
          const inner = 0.5 * Math.min(bounds.width, bounds.height);
          const outer = 0.95 * Math.max(bounds.width, bounds.height);
          const range = Math.max(1, outer - inner);
          const nextProximity = 1 - Math.min(1, Math.max(0, (distance - inner) / range));
          const wasInvisible = proximity === 0 && targetProximity === 0;
          targetProximity = nextProximity;

          if (nextProximity > 0 && distance > CENTRE_GUARD_PX) {
            const nextAngle = Math.atan2(dy, dx);
            targetAngle = nextAngle;
            if (wasInvisible) angle = nextAngle;
          }
        } else {
          targetProximity = 0;
        }
      }

      if (!ambient && proximity === 0 && targetProximity === 0) {
        previousTimestamp = null;
        return;
      }

      const elapsed =
        previousTimestamp === null
          ? 1000 / 60
          : Math.min(64, Math.max(0, timestamp - previousTimestamp));
      previousTimestamp = timestamp;
      const alpha = 1 - Math.exp(-elapsed / FOLLOW_TIME_MS);
      const angleDelta = Math.atan2(
        Math.sin(targetAngle - angle),
        Math.cos(targetAngle - angle)
      );
      const proximityDelta = targetProximity - proximity;
      const angleSettled = Math.abs(angleDelta) < SETTLE_THRESHOLD;
      const proximitySettled = Math.abs(proximityDelta) < SETTLE_THRESHOLD;

      angle = angleSettled ? targetAngle : angle + angleDelta * alpha;
      proximity = proximitySettled ? targetProximity : proximity + proximityDelta * alpha;

      if (!ambient) {
        writePointer();
        if (
          (targetProximity === 0 && proximity === 0) ||
          (angleSettled && proximitySettled && pendingPointer === null)
        ) {
          previousTimestamp = null;
          return;
        }
        frame = requestAnimationFrame(paint);
        return;
      }

      updateAmbient(timestamp, elapsed);
      // Quiet phases write at ~10fps; a sweep or an active pointer writes
      // every frame so the fast pass stays crisp.
      const pointerActive = pointerWeight > 0 || proximity > 0 || targetProximity > 0;
      if (
        pointerActive ||
        sweeping ||
        timestamp - lastAmbientWrite >= AMBIENT_QUIET_FRAME_MS
      ) {
        writeBlended();
        lastAmbientWrite = timestamp;
      }
      // Full rate only while something is actually moving fast. Otherwise the
      // next moment the loop MUST be awake is the start of a planned sweep or
      // flick, or else the end of the quiet phase.
      if (pointerActive || sweeping) frame = requestAnimationFrame(paint);
      else scheduleQuietTick((sweep ? sweep.startAt : quietUntil) - timestamp);
    };

    const move = (event: PointerEvent) => {
      if (!hasSettledRef.current) return;
      if (!media.matches || event.pointerType !== 'mouse' || document.hidden) return;
      pendingPointer = { clientX: event.clientX, clientY: event.clientY };
      schedule();
    };

    const fadeOut = () => {
      pendingPointer = null;
      targetProximity = 0;
      if (proximity === 0 && !ambientOn()) {
        if (frame !== null) cancelAnimationFrame(frame);
        frame = null;
        previousTimestamp = null;
        highlight.setAttribute('opacity', '0');
        return;
      }
      schedule();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) hideImmediately();
      else if (ambientOn()) schedule();
    };

    // The ambient loop starts once the entrance has settled: a light poll
    // (rAF, no writes) until then, then the loop proper. Not under reduced
    // motion, where the glint stays static.
    const waitForSettle = () => {
      if (!ambientEnabled) return;
      if (hasSettledRef.current) {
        schedule();
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = null;
        waitForSettle();
      });
    };

    const detach = () => {
      hideImmediately();
      document.removeEventListener('pointermove', move);
      document.documentElement.removeEventListener('pointerleave', fadeOut);
      document.removeEventListener('pointercancel', fadeOut);
      window.removeEventListener('blur', fadeOut);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    const syncPreference = () => {
      detach();
      document.addEventListener('visibilitychange', handleVisibilityChange);
      if (media.matches) {
        document.addEventListener('pointermove', move, { passive: true });
        document.documentElement.addEventListener('pointerleave', fadeOut);
        document.addEventListener('pointercancel', fadeOut);
        window.addEventListener('blur', fadeOut);
      }
      waitForSettle();
    };

    syncPreference();
    media.addEventListener('change', syncPreference);
    return () => {
      detach();
      media.removeEventListener('change', syncPreference);
    };
  }, [
    markRef,
    highlightRef,
    directionRef,
    sheenRef,
    edgeLightRef,
    edgeShadowRef,
    hasSettledRef,
    ambientEnabled,
    baseOpacity.sheenNear,
    baseOpacity.sheenMid,
    baseOpacity.edgeLightPeak,
  ]);
};
